import { useLang } from '../i18n/LangContext';
import { useNavigate } from 'react-router-dom';
import { atlasToday, greet, pathForView, type AtlasToday as AtlasTodayData, type AtlasTodayRow, type Role, type ViewId } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { ErrorState, Skeleton } from '../ui/states';
import { sx } from '../ui/designStyles';
import { RoundSection } from '../pages/companion/RichSections';
import { BuildLabel } from '../ui/buildLabels';

/**
 * The Atlas panel's Today tab (Console V5; design T25117-25186, tdLayer V5 L41525-41640):
 * the default tab for staff roles. Team: calendar, overnight, likely next and Missions.
 * Facility Admin: survey readiness, credentials expiring, tonight. Care Staff: who to see
 * first, what is due, the handoff.
 *
 * Real buildings, every role: GET /atlas/today (src/aithora/v3/atlas/today.ts) composes the
 * caller's own Today from Guardian, the Worklist, the shift handoffs, the care plans, the
 * Truth Ledger and System Pulse, ranked on the server. Sections with no source (calendar,
 * survey readiness, credentials, likely next, Missions) are not drawn.
 *
 * A seeded demo building: the design's own sample under a SAMPLE · NOT BUILT YET label.
 *
 * Its buttons only open the page they name; nothing here writes, marks done or sends.
 */

type Act = { go: ViewId } | { tab: 'chat' | 'act' } | null;
type Row = [lead: string, text: string, sub: string, btn: string, act: Act];
interface Sec { title: string; meta: string; modeled?: boolean; rows: Row[]; empty?: string }
interface Mission { name: string; steps: [string, boolean][]; next: string; btn: string }

const B = 'flex:0 0 auto;display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;';
const BTN_S = B + 'border:1px solid rgba(34,39,46,.2);color:#22272E;';
const BTN_OFF = B + 'background:rgba(34,39,46,.06);color:#54606C;cursor:default;';
const MOD = 'font-size:14px;font-weight:800;letter-spacing:.06em;color:#2F5C97;border:1px solid rgba(47,92,151,.4);border-radius:4px;padding:1px 5px;';
const CAP = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const SAMPLE = 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.08);white-space:nowrap;';

function content(role: Role | null, hello: string): { head: string; sub: string; secs: Sec[]; missions: Mission[]; foot: string } {
  if (role === 'CAREGIVER' || role === 'STAFF') {
    return {
      head: hello, sub: 'Memory Care · East hall · 3 PM to 11 PM · 16 rooms · Atlas ranks who to see first and why.',
      secs: [
        { title: 'See first this shift', meta: 'ranked by risk', modeled: true, rows: [
          ['Rm 214', 'Beatrice Okoro', 'Resident emergency, nobody has claimed it. Gait speed was already down 18% in 14 days.', 'Respond', { go: 'guardian' }],
          ['Rm 212', 'Dorothy Meyer', 'Delirium signs since 6:40 PM, flagged by the detector. Offer fluids; a CAM screen is due at 8.', 'Go see', { go: 'worklist' }],
          ['Rm 116', 'Walter Byrne', 'New admit. Intake set, no fluids logged since 2:10 PM.', 'Log water', null],
        ] },
        { title: 'Due on your shift', meta: '', rows: [
          ['7:30 PM', 'Robert Chen · neuro check 2 of 4', 'Post-fall sequence. A missed step restarts it.', 'Open', { go: 'worklist' }],
          ['9:00 PM', 'Mino Hotaru · Metformin 500 mg', 'Refused the 5 PM dose. After 9 it counts as missed.', 'Open', { go: 'worklist' }],
          ['10:45 PM', 'Handoff note for the night shift', 'Talk it through; Atlas drafts it and you sign.', 'Start with Scribe', { go: 'atlas' }],
        ] },
        { title: 'From the day shift', meta: 'signed 2:58 PM by J. Patel', rows: [
          ['', 'Eleanor Voss had PRN lorazepam at 6:10 PM; the follow-up check is still open.', '', '', null],
          ['', 'Ada Reyes: wound check on the left heel is overdue.', '', '', null],
        ] },
      ],
      missions: [],
      foot: 'Atlas suggests; you decide. Nothing on a resident’s care plan changes without a nurse’s signature.',
    };
  }
  if (role === 'ADMIN') {
    return {
      head: hello, sub: 'C C Young · survey readiness, credentials and tonight, in one place.',
      secs: [
        { title: 'Survey readiness · 86%', meta: '3 gaps before the Nov window', rows: [
          ['', 'Fire drill log missing for September, night shift', 'Life Safety K-tag. Last drill logged Aug 22.', 'Assign to maintenance', null],
          ['', '2 care plans past their 90-day review', 'Walter Byrne, Mae Castillo.', 'Open residents', { go: 'residents' }],
          ['', 'Grievance log: 1 entry with no written response', 'Filed Sep 26 by a family member. Response due in 5 days.', 'Open', { go: 'policy' }],
        ] },
        { title: 'Credentials expiring', meta: 'next 30 days', rows: [
          ['12 days', 'M. Ortiz · CNA license', 'Texas HHS renewal.', 'Send reminder', null],
          ['21 days', 'J. Patel · CPR certification', '', 'Send reminder', null],
          ['28 days', 'R. Lee · TB test', '', 'Send reminder', null],
        ] },
        { title: 'Tonight', meta: '', modeled: true, rows: [
          ['', '2 residents at higher fall risk', 'Robert Chen (fell today) and Walter Byrne (new admit). Night aides have them first on their list.', 'Open', { go: 'residents' }],
          ['', 'Night shift is 1 aide below your ratio from 11 PM', 'Two staff swapped shifts; nobody has picked up.', 'Open Worklist', { go: 'worklist' }],
        ] },
      ],
      missions: [{ name: 'Be survey-ready before the November window', steps: [['Life Safety binder', true], ['Infection control audit', true], ['Care plan reviews', false], ['Fire drill logs', false], ['Mock survey walk', false]], next: 'Send the 2 overdue care-plan reviews to the nurse manager with a Friday deadline.', btn: 'Draft it' }],
      foot: 'Only C C Young. Reminders go to each person’s own phone and are logged.',
    };
  }
  return {
    head: hello, sub: 'Your day, what Atlas did overnight and what is likely next.',
    secs: [
      { title: 'On your calendar', meta: 'today', rows: [
        ['9:00 AM', 'Belterra kickoff follow-up', 'Prep is ready: last call notes, open items and their administrator’s questions.', 'Open prep', { go: 'sales' }],
        ['11:30 AM', 'Freddy · LeadingAge travel', 'Flight booking is waiting for your OK ($1,840).', 'Review', { tab: 'chat' }],
        ['2:00 PM', 'Center at Parmer go-live check', '14 of 20 devices paired.', 'Open', { go: 'overview' }],
      ] },
      { title: 'Overnight', meta: 'while you were away', rows: [
        ['', 'Scored 1,412 facilities on the nightly pass', '', '', null],
        ['', 'Sent 2 emails under your rules; nobody pressed Undo', '', 'See activity', { tab: 'act' }],
        ['', 'Refused 1 reminder that would have changed a dose', 'Out of scope: medication changes need a clinician.', '', null],
      ] },
      { title: 'Likely next', meta: '', modeled: true, rows: [
        ['', 'Freddy hits his AI cap around 9:40 PM', 'He is at $11.40 of $20 today.', 'Raise cap', { go: 'settings' }],
        ['', 'Shady Acres Atlas silent 31h: likely a powered-off tablet', 'Same pattern as Aug 14; fixed by a call to the front desk.', 'Open System Pulse', { go: 'pulse' }],
      ] },
    ],
    missions: [
      { name: 'Get Center at Parmer live by Oct 6', steps: [['BAA signed', true], ['Staff accounts', true], ['Devices paired 14/20', false], ['Family invites', false], ['Go-live check', false]], next: 'Text their administrator the 6 unpaired room numbers and a pairing guide.', btn: 'Draft it' },
      { name: 'Close Gracy Woods pilot paperwork', steps: [['Pilot terms', true], ['BAA renewal call', false], ['FOUNDING50 code', false], ['Signed order form', false]], next: 'Move the BAA renewal call to Tuesday 10:00 as their administrator asked.', btn: 'Review' },
    ],
    foot: 'Likely-next items are predictions from your own data, not facts. Missions never spend money or touch care; those always ask.',
  };
}

/** Which roles get the Today tab (tdOk: everyone but Resident, Family, Aithora Home, Partner and Sales). */
export const hasToday = (role: Role | null): boolean => !!role && !['RESIDENT', 'FAMILY_MEMBER', 'MEMBER', 'PARTNER', 'SALES'].includes(role);

/**
 * The design's sample people and business are fictional. Outside a seeded demo building
 * nobody sees them (gap audit 2026-10-12; Andrew 2026-10-12 "we need to see it really
 * work"): every role reads its real Today from GET /atlas/today instead.
 */
export function showsTodaySample(demoBuilding: boolean): boolean {
  return demoBuilding;
}

/** The real Today's foot line, per persona. */
const REAL_FOOT: Record<AtlasTodayData['persona'], string> = {
  care: 'Atlas suggests; you decide. Nothing on a resident’s care plan changes without a nurse’s signature.',
  admin: 'From your building’s own records. Each row opens the page that acts on it; nothing here writes.',
  team: 'From the Truth Ledger and System Pulse. Nothing here writes.',
};

/** A real Today row's action: a console page, or the panel's own tab. */
export function actOf(r: AtlasTodayRow): Act {
  if (r.go) return { go: r.go };
  if (r.tab) return { tab: r.tab };
  return null;
}

export function AtlasToday({ role, name, onTab, onClose }: { role: Role | null; name: string; onTab: (t: 'chat' | 'act') => void; onClose: () => void }) {
  const navigate = useNavigate();
  const { session, client } = useSession();
  const sample = showsTodaySample(session?.user.demoBuilding === true);
  // Every role's Atlas opens on the person's own greeting, by their building's clock (Andrew, 2026-10-19).
  const { lang } = useLang();
  const full = content(role, greet(name, new Date(), session?.user.timeZone ?? null, lang === 'es' ? 'es' : 'en'));
  const real = useQuery((signal) => atlasToday(client, { signal }), [client, session?.token], { enabled: !sample, pollMs: 60_000 });
  const d = real.data;
  const c = sample
    ? full
    : {
        ...full,
        sub: d ? d.sub : '',
        secs: (d?.sections ?? []).map((s): Sec => ({ title: s.title, meta: s.meta, empty: s.empty, rows: s.rows.map((r): Row => [r.lead, r.text, r.sub, r.btn, actOf(r)]) })),
        missions: [],
        foot: d ? REAL_FOOT[d.persona] : '',
      };
  const run = (a: Act) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!a) return;
    if ('tab' in a) onTab(a.tab);
    else { onClose(); navigate(pathForView(a.go)); }
  };
  return (
    <div data-screen-label="Atlas Today" style={sx('display:flex;flex-direction:column;gap:14px;min-width:0;')}>
      <div>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;letter-spacing:-.01em;')}>{c.head}</span>
          <span style={sx('flex:1 1 10px;')} />
          {sample && <BuildLabel><span style={sx(SAMPLE)}>SAMPLE · NOT BUILT YET</span></BuildLabel>}
        </div>
        {c.sub && <div style={sx('font-size:14px;color:#54606C;margin-top:2px;line-height:1.45;')}>{c.sub}</div>}
      </div>
      {!sample && !d && real.loading && <Skeleton rows={4} label="Loading your Today" />}
      {!sample && real.error && <ErrorState error={real.error} onRetry={real.reload} />}
      {c.secs.map((sec) => (
        <div key={sec.title} style={sx('display:flex;flex-direction:column;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-bottom:2px;')}>
            <span style={sx(CAP)}>{sec.title}</span>
            {sec.modeled && <BuildLabel><span style={sx(MOD)}>MODELED</span></BuildLabel>}
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>{sec.meta}</span>
          </div>
          {sec.rows.map(([lead, text, sub, btn, act], i) => (
            <div key={i + text} style={sx('display:flex;align-items:flex-start;gap:10px;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
              {lead && <span style={sx('flex:0 0 56px;font-size:14px;font-weight:700;color:#264C7E;font-variant-numeric:tabular-nums;padding-top:1px;')}>{lead}</span>}
              <div style={sx('flex:1 1 auto;min-width:0;')}>
                <div style={sx('font-size:16px;font-weight:600;line-height:1.4;text-wrap:pretty;color:#22272E;')}>{text}</div>
                {sub && <div style={sx('font-size:14px;color:#54606C;margin-top:2px;line-height:1.4;text-wrap:pretty;')}>{sub}</div>}
              </div>
              {btn && (
                <a href="#" onClick={run(act)} aria-disabled={!act || undefined} title={act ? undefined : 'Sample: not built yet'} style={sx(act ? BTN_S : BTN_OFF)}>{btn}</a>
              )}
            </div>
          ))}
          {sec.rows.length === 0 && sec.empty && (
            <div style={sx('padding:8px 0;border-top:1px solid rgba(34,39,46,.08);font-size:16px;color:#54606C;line-height:1.4;')}>{sec.empty}</div>
          )}
        </div>
      ))}
      {/* V6 "Symptom check result" (Care Staff): no route serves a resident's symptom check to
          staff yet, so it says none has come in rather than drawing the design's sample. */}
      {(role === 'CAREGIVER' || role === 'STAFF') && (
        <div data-screen-label="Symptom check result" style={sx('flex:0 0 auto;display:grid;grid-template-columns:minmax(0,1fr);gap:6px;padding:12px 14px;margin:0 0 12px;border-radius:12px;background:#FFFFFF;border:1px solid rgba(34,39,46,.14);')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
            <span style={sx('font-size:14px;letter-spacing:.12em;text-transform:uppercase;font-weight:800;color:#54606C;white-space:nowrap;')}>Symptom check</span>
            <span style={sx('flex:1 1 auto;')} />
            <BuildLabel><span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.08);border-radius:4px;padding:2px 6px;')}>NOT CONNECTED</span></BuildLabel>
          </div>
          <div style={sx('font-size:13.5px;line-height:1.45;color:#3C454F;')}>No symptom check has come in from your residents this shift.</div>
        </div>
      )}
      {/* V6 tdRoundShow: Care Staff's "Before your round" moved here from the resident Atlas page; real data. */}
      {(role === 'CAREGIVER' || role === 'STAFF') && <RoundSection />}
      {c.missions.length > 0 && (
        <div style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
            <span style={sx(CAP)}>Missions</span>
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>Atlas works the steps; anything that writes asks you first</span>
          </div>
          {c.missions.map((mi) => {
            const n = mi.steps.filter((x) => x[1]).length;
            const pct = Math.round((100 * n) / mi.steps.length);
            return (
              <div key={mi.name} style={sx('border:1px solid rgba(34,39,46,.14);border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:7px;background:#FCFAF4;min-width:0;')}>
                <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:13.5px;font-weight:800;color:#22272E;flex:1 1 auto;min-width:0;')}>{mi.name}</span>
                  <span style={sx('font-size:14px;font-weight:700;color:#264C7E;font-variant-numeric:tabular-nums;')}>{pct + '%'}</span>
                </div>
                <div style={sx('height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;')}>
                  <div style={sx('height:100%;width:' + pct + '%;background:#264C7E;')} />
                </div>
                <div style={sx('display:flex;flex-wrap:wrap;gap:4px 12px;')}>
                  {mi.steps.map(([t, done], i) => (
                    <span key={t} style={sx('font-size:14px;white-space:nowrap;color:' + (done ? '#1F6B52' : i === n ? '#22272E;font-weight:700' : '#54606C') + ';')}>{(done ? '✓' : i === n ? '●' : '○') + ' ' + t}</span>
                  ))}
                </div>
                <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;border-top:1px solid rgba(34,39,46,.08);padding-top:7px;')}>
                  <span style={sx('font-size:14px;color:#22272E;flex:1 1 180px;min-width:0;line-height:1.4;')}><strong>Next:</strong> {mi.next}</span>
                  <a href="#" onClick={(e) => e.preventDefault()} aria-disabled="true" title="Sample: not built yet" style={sx(BTN_OFF)}>{mi.btn}</a>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={sx('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{c.foot}</div>
    </div>
  );
}

